import { toSlug } from '@pulsify/shared/slug'
import type { Listing } from '~/shared/types'

const MIN_DESCRIPTION_CHARS = 120
const MIN_PHOTOS = 3

export type ReadinessItem =
  | 'description'
  | 'hours'
  | 'photos'
  | 'phone'
  | 'website'
  | 'email'
  | 'instagram'

interface Readiness {
  score: number
  missing: ReadinessItem[]
}

function plainLength(html: string): number {
  return html
    .replace(/<[^>]*>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim().length
}

function photoCount(listing: Listing): number {
  const urls = new Set(listing.images?.map((image) => image.url) ?? [])
  if (listing.coverImageUrl) urls.add(listing.coverImageUrl)
  return urls.size
}

/** How complete a place is for search engines and AI answers, and what it still lacks. */
export function placeReadiness(listing: Listing): Readiness {
  const checks: [ReadinessItem, boolean][] = [
    ['description', plainLength(listing.description ?? '') >= MIN_DESCRIPTION_CHARS],
    ['hours', (listing.hours?.length ?? 0) > 0],
    ['photos', photoCount(listing) >= MIN_PHOTOS],
    ['phone', !!listing.phone?.trim()],
    ['website', !!listing.website?.trim()],
    ['email', !!listing.email?.trim()],
    ['instagram', !!listing.instagram?.trim()],
  ]
  const done = checks.filter(([, ok]) => ok).length
  return {
    score: Math.round((done / checks.length) * 100),
    missing: checks.filter(([, ok]) => !ok).map(([item]) => item),
  }
}

const BADGES = {
  de: { file: 'auf-pulsify-finden', alt: 'Auf Pulsify finden', width: 204 },
  en: { file: 'find-us-on-pulsify', alt: 'Find us on Pulsify', width: 182 },
} as const

/** The "Auf Pulsify finden" link a place pastes into its own website. */
export function badgeHtml(listing: Listing, locale: 'de' | 'en', siteUrl: string): string {
  const base = siteUrl.replace(/\/$/, '')
  const badge = BADGES[locale]
  const href = `${base}/${locale}/locations/${toSlug(listing.name, listing.id)}`
  return `<a href="${href}" target="_blank" rel="noopener"><img src="${base}/badge/${badge.file}.png" alt="${badge.alt}" width="${badge.width}" height="44"></a>`
}

/** Where the badge image lives, for the preview. */
export function badgeImageUrl(locale: 'de' | 'en', siteUrl: string): string {
  return `${siteUrl.replace(/\/$/, '')}/badge/${BADGES[locale].file}.png`
}
